import { useId, useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Popover } from './linear/Popover';
import { useLabels } from '../../hooks/useLabels';

interface Props {
  value: string[];
  onChange: (labelIds: string[]) => void;
  label?: string;
  className?: string;
}

/**
 * Labels as a dropdown in the task form: the trigger shows the chosen labels
 * as coloured chips, the panel lists every label with a search box and a
 * checkbox. Replaces the wall of pills that grew with every new label.
 * Unlike the filter MultiSelect, an empty selection means "no labels".
 */
export function LabelSelect({ value, onChange, label = 'Etichete', className }: Props) {
  const labelId = `${useId()}-label`;
  const { data: labels } = useLabels();
  const [query, setQuery] = useState('');

  const all = useMemo(() => labels ?? [], [labels]);
  const selected = all.filter((l) => value.includes(l.id));
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('ro');
    return q ? all.filter((l) => l.name.toLocaleLowerCase('ro').includes(q)) : all;
  }, [all, query]);

  if (all.length === 0) return null;

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  return (
    <div className={className}>
      <span id={labelId} className="ui-field-label">
        {label}
      </span>
      <Popover
        width={260}
        matchTriggerWidth
        trigger={({ open, toggle: togglePopover, ref }) => (
          <button
            type="button"
            ref={(el) => ref(el)}
            onClick={() => {
              if (!open) setQuery('');
              togglePopover();
            }}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-labelledby={labelId}
            className="ui-select-trigger min-h-[38px] flex-wrap justify-start gap-1 py-1.5"
          >
            {selected.length === 0 ? (
              <span className="text-gray-400">Alege etichete…</span>
            ) : (
              selected.map((l) => (
                <span
                  key={l.id}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-fixed-white"
                  style={{ backgroundColor: l.color }}
                >
                  {l.name}
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Scoate ${l.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(l.id);
                    }}
                    className="opacity-80 hover:opacity-100"
                  >
                    <X size={11} />
                  </span>
                </span>
              ))
            )}
            <ChevronDown size={14} className="ml-auto shrink-0 opacity-60" />
          </button>
        )}
      >
        {() => (
          <div>
            <div className="flex items-center gap-2 border-b border-gray-200 px-2 py-1.5">
              <Search size={14} className="shrink-0 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Caută eticheta…"
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1" role="listbox" aria-multiselectable>
              {filtered.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400">Nicio etichetă.</p>
              )}
              {filtered.map((l) => {
                const isOn = value.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    role="option"
                    aria-selected={isOn}
                    onClick={() => toggle(l.id)}
                    className="ui-menu-item justify-between"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: l.color }}
                      />
                      <span className="truncate">{l.name}</span>
                    </span>
                    {isOn && <Check size={14} className="shrink-0 text-brand-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
}
